import React, { useState } from 'react'
import {
    Box,
    Button,
    Card, CardBody, CardHeader,
    VStack, HStack,
    Text,
    Flex,
    Heading,
    Container,
    Divider,
    IconButton,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useBreakpointValue,
    useDisclosure,
    Icon,
    Badge,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    Textarea,
    CircularProgress,
    Center
  } from '@chakra-ui/react';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  HamburgerIcon,
  ArrowBackIcon,
  StarIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import { ModernSidebar, UserMenu, BreadcrumbNav, useMobileBottomPadding, MobileBottomNav } from './SharedComponents';
import API_URL from '../../config';

// Helper component to format text with bullets, icons, and visual elements
const FormattedText = ({ children, fontSize = { base: 'sm', md: 'md' }, type = 'default' }) => {
  if (!children) return null;

  // Split by double line breaks for paragraphs
  const paragraphs = children.split(/\n\n+/);

  // Color schemes based on content type
  const colorSchemes = {
    description: { bg: 'blue.50', border: 'blue.400', icon: 'blue.500', bullet: 'blue.600' },
    instructions: { bg: 'brand.50', border: 'brand.400', icon: 'brand.500', bullet: 'brand.600' },
    additional: { bg: 'purple.50', border: 'purple.400', icon: 'purple.500', bullet: 'purple.600' },
    default: { bg: 'gray.50', border: 'brand.400', icon: 'brand.500', bullet: 'brand.600' }
  };

  const colors = colorSchemes[type] || colorSchemes.default;

  return (
    <VStack align="stretch" spacing={{ base: 3, md: 4 }}>
      {paragraphs.map((paragraph, idx) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return null;

        // Check if it's a bullet point
        if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
          const bulletText = trimmed.replace(/^[•-]\s*/, '');
          return (
            <Box
              key={idx}
              bg={colors.bg}
              p={{ base: 3, md: 4 }}
              borderRadius="lg"
              borderLeft="4px"
              borderColor={colors.border}
              transition="all 0.2s"
              _hover={{
                transform: 'translateX(4px)',
                shadow: 'md'
              }}
            >
              <Text
                fontSize={fontSize}
                color="gray.800"
                lineHeight="tall"
                fontWeight="500"
              >
                {bulletText}
              </Text>
            </Box>
          );
        }

        // Regular paragraph with subtle background
        return (
          <Box
            key={idx}
            p={{ base: 2, md: 3 }}
            borderRadius="md"
            bg="gray.50"
          >
            <Text
              fontSize={fontSize}
              color="gray.700"
              lineHeight="tall"
              fontWeight="400"
            >
              {trimmed}
            </Text>
          </Box>
        );
      })}
    </VStack>
  );
};

const PracticeView = ({ state, onRouteChange, onContinueChapter, onNavigateExercise, onToggleExerciseCompletion, getOverview, onRandomChapter, toggleSidebar, setLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { onOpen: onSearchOpen } = useDisclosure()
  const { onOpen: onProfileOpen } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const bottomPadding = useMobileBottomPadding()

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Navigation */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color="gray.700" />
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange}
                getOverview={getOverview}
                onRandomChapter={onRandomChapter}
                toggleSidebar={undefined}
                onClose={onClose}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      
      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box 
            w="280px" 
            bg="white" 
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm" 
            minH="100vh" 
            position="fixed" 
            left={0} 
            top={0}
            zIndex={10}
            transition="all 0.3s ease"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              getOverview={getOverview}
              onRandomChapter={onRandomChapter}
              toggleSidebar={toggleSidebar}
            />
          </Box>
        )}
        
        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease" minW={0} overflow="hidden">
          {/* Top Navigation */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 3, md: 3 }}>
            <Flex align="center" justify="space-between">
              <HStack spacing={3}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                  />
                )}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    size="sm"
                    _hover={{
                      color: 'brand.700',
                      bg: 'brand.50'
                    }}
                  />
                )}
                {!isMobile && (
                  <>
                    <Button
                      leftIcon={<ArrowBackIcon />}
                      variant="ghost"
                      colorScheme="brand"
                      onClick={() => onRouteChange('practicesView')}
                    >
                      Back to Practices
                    </Button>
                    <VStack align="start" spacing={0}>
                      <Heading size="lg" color="gray.800">
                        Today's Practice
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Focus on your growth
                      </Text>
                    </VStack>
                  </>
                )}
              </HStack>

              <UserMenu onRouteChange={onRouteChange} username={state.username} />
            </Flex>
          </Box>
          
          {/* Breadcrumb Navigation */}
          <BreadcrumbNav 
            route={state.route} 
            goalName={state.page?.goalName}
            exerciseName={state.page?.name}
            goalId={state.page?.goal_id}
            onRouteChange={onRouteChange} 
          />
          
          {/* Practice Content */}
          <PracticeContent
            state={state}
            onContinueChapter={onContinueChapter}
            onNavigateExercise={onNavigateExercise}
            onToggleExerciseCompletion={onToggleExerciseCompletion}
            bottomPadding={bottomPadding}
          />
        </Box>
      </Flex>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentRoute="practice"
        onRouteChange={onRouteChange}
        onRandomChapter={onRandomChapter}
        onSearchOpen={onSearchOpen}
        onProfileOpen={onProfileOpen}
      />
    </Box>
  )
}

const PracticeContent = ({ state, onContinueChapter, onNavigateExercise, onToggleExerciseCompletion, bottomPadding }) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [reflection, setReflection] = useState('');
  const [showAdditionalContent, setShowAdditionalContent] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompletingPractice, setIsCompletingPractice] = useState(false);
  const [, setShowFeelingSelector] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [showFeedbackCard] = useState(true);
  const [showFeedbackContent, setShowFeedbackContent] = useState(true);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [savedFeedbackText, setSavedFeedbackText] = useState('');
  const [navigationState, setNavigationState] = useState({
    hasPrevious: false,
    hasNext: false,
    isCheckingNavigation: true,
    isNavigating: false
  });
  const toast = useToast();
  
  const page = state.page
  const practice = page?.items?.[0] || page
  
  // Check navigation availability when practice loads
  React.useEffect(() => {
    if (practice && practice.id) {
      checkNavigationAvailability(practice.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice?.id]);

  const checkNavigationAvailability = async (exerciseId) => {
    setNavigationState(prev => ({ ...prev, isCheckingNavigation: true }));
    
    try {
      // Check if previous exercise exists
      const prevResponse = await fetch(`${API_URL}/navigate_exercise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exercise_id: exerciseId, direction: 'previous', username: state.username })
      });
      const prevData = await prevResponse.json();

      // Check if next exercise exists
      const nextResponse = await fetch(`${API_URL}/navigate_exercise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exercise_id: exerciseId, direction: 'next', username: state.username })
      });
      const nextData = await nextResponse.json();

      setNavigationState({
        hasPrevious: prevData.success,
        hasNext: nextData.success,
        isCheckingNavigation: false,
        isNavigating: false
      });
    } catch (error) {
      console.error('Error checking navigation availability:', error);
      setNavigationState({ 
        hasPrevious: false, 
        hasNext: false, 
        isCheckingNavigation: false,
        isNavigating: false 
      });
    }
  };

  const handleNavigate = async (direction) => {
    if (!practice || !practice.id || !onNavigateExercise || navigationState.isNavigating) return;
    
    setNavigationState(prev => ({ ...prev, isNavigating: true }));
    
    try {
      await onNavigateExercise(practice.id, direction);
    } catch (error) {
      console.error(`Error navigating to ${direction} exercise:`, error);
      toast({
        title: 'Navigation Failed',
        description: `Unable to navigate to ${direction} exercise. Please try again.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setNavigationState(prev => ({ ...prev, isNavigating: false }));
    }
  };
  
  if (!practice) {
    return (
      <Center h="200px">
        <VStack spacing={4}>
          <CircularProgress isIndeterminate color="brand.400" />
          <Text color="gray.600">Loading practice...</Text>
        </VStack>
      </Center>
    )
  }
  
  const practiceCompleted = practice.completed || false;
  const generatedContent = practice.generated_content || '';

  const handleComplete = async () => {
    setIsCompletingPractice(true);
    try {
      const response = await fetch(`${API_URL}/toggle_exercise_completion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: practice.id,
          completed: true,
          username: state.username
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setIsCompleted(true);
        toast({
          title: "Practice Completed! 🎉",
          description: "Great work on completing today's practice.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        
        // Show reflection modal after a brief delay
        setTimeout(() => setShowReflection(true), 1000);
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to mark practice as completed",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Complete practice error:', error);
      toast({
        title: "Error",
        description: "Failed to complete practice. Please check your connection and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCompletingPractice(false);
    }
  };

  const handleGenerateContent = async (userFeeling = null) => {
    setIsGenerating(true);
    setShowFeelingSelector(false);

    try {
      const response = await fetch(`${API_URL}/generate_content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exercise_id: practice.id,
          username: state.username,
          force_regenerate: true,
          user_feeling: userFeeling
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh the practice data to get the updated content
        onContinueChapter(practice.id);
        toast({
          title: "Background Context Generated",
          description: "Learn why this exercise matters for your growth.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to generate content",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate content",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!feedback.trim()) {
      toast({
        title: "Feedback is empty",
        description: "Please write some feedback before saving.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSavingFeedback(true);
    try {
      const response = await fetch(`${API_URL}/save_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exercise_id: practice.id,
          feedback: feedback,
          username: state.username
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Feedback Saved",
          description: "Your feedback will help personalize future exercises.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setSavedFeedbackText(feedback);
        setFeedbackSaved(true);
        setFeedback('');
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to save feedback",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save feedback. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingFeedback(false);
    }
  };


  const currentDay = (practice.part || 0) + 1;
  const estimatedTime = practice.time || '10 mins';

  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }} pb={bottomPadding} px={{ base: 3, md: 4 }}>
      <VStack spacing={{ base: 4, md: 8 }} align="stretch">
        {/* Practice Header */}
        <Card bg="white" shadow="lg" borderTop="4px" borderColor="brand.500">
          <CardHeader bg="brand.50" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
            <VStack spacing={{ base: 3, md: 4 }}>
              <HStack justify="space-between" w="full" wrap="wrap" gap={2}>
                <HStack spacing={{ base: 1.5, md: 3 }}>
                  <Badge colorScheme="brand" variant="solid" borderRadius="full" px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                    Day {currentDay}
                  </Badge>
                  <Badge colorScheme="gray" variant="outline" borderRadius="full" px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                    <HStack spacing={1}>
                      <TimeIcon boxSize={{ base: 2.5, md: 3 }} />
                      <Text>{estimatedTime}</Text>
                    </HStack>
                  </Badge>
                </HStack>

                {practiceCompleted || isCompleted ? (
                  <Badge colorScheme="green" variant="solid" borderRadius="full" px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                    <HStack spacing={1}>
                      <CheckIcon boxSize={{ base: 2.5, md: 3 }} />
                      <Text>Done</Text>
                    </HStack>
                  </Badge>
                ) : (
                  <Badge colorScheme="brand" variant="solid" borderRadius="full" px={{ base: 2, md: 3 }} py={1} fontSize={{ base: 'xs', md: 'sm' }}>
                    In Progress
                  </Badge>
                )}
              </HStack>

              <Heading size={{ base: 'sm', md: 'lg' }} color="gray.800" textAlign="center">
                {practice.name}
              </Heading>
            </VStack>
          </CardHeader>

          <CardBody p={{ base: 4, md: 8 }}>
            <VStack spacing={{ base: 4, md: 6 }} align="stretch">
              <Box>
                <HStack spacing={2} mb={{ base: 2, md: 3 }}>
                  <Icon as={StarIcon} color="blue.500" boxSize={{ base: 3, md: 4 }} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color="blue.600" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                    Why This Practice Matters
                  </Text>
                </HStack>
                <FormattedText fontSize={{ base: 'sm', md: 'md' }} type="description">
                  {practice.description}
                </FormattedText>
              </Box>

              <Divider borderColor="gray.300" />

              <Box>
                <HStack spacing={2} mb={{ base: 2, md: 3 }}>
                  <Icon as={CheckIcon} color="brand.500" boxSize={{ base: 3, md: 4 }} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color="brand.600" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                    Your Practice Instructions
                  </Text>
                </HStack>
                <FormattedText fontSize={{ base: 'sm', md: 'md' }} type="instructions">
                  {practice.instructions}
                </FormattedText>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Background Section - Optional contextual information */}
        {generatedContent && (
          <Card bg="white" shadow="lg" borderLeft="4px" borderColor="purple.400">
            <CardHeader py={{ base: 3, md: 4 }} px={{ base: 4, md: 6 }}>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Text fontSize={{ base: 'lg', md: 'xl' }}>💡</Text>
                  <Heading size={{ base: 'xs', md: 'md' }} color="gray.800">
                    Background
                  </Heading>
                </HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  onClick={() => setShowAdditionalContent(!showAdditionalContent)}
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  {showAdditionalContent ? 'Hide' : 'Show'}
                </Button>
              </HStack>
            </CardHeader>

            {showAdditionalContent && (
              <CardBody pt={0} pb={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
                <VStack spacing={4} align="stretch">
                  <Box
                    bg="purple.50"
                    p={{ base: 4, md: 5 }}
                    borderRadius="lg"
                    lineHeight="tall"
                  >
                    <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.700" whiteSpace="pre-wrap">
                      {generatedContent}
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={() => handleGenerateContent(null)}
                    isLoading={isGenerating}
                    loadingText="Generating..."
                    alignSelf="flex-start"
                  >
                    Generate alternative context
                  </Button>
                </VStack>
              </CardBody>
            )}
          </Card>
        )}

        {/* Feedback Card */}
        {showFeedbackCard && (
          <Card bg="white" shadow="lg" borderLeft="4px" borderColor="orange.400">
            <CardHeader py={{ base: 3, md: 4 }} px={{ base: 4, md: 6 }}>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Text fontSize={{ base: 'lg', md: 'xl' }}>💭</Text>
                  <Heading size={{ base: 'xs', md: 'md' }} color="gray.800">
                    Feedback on this exercise
                  </Heading>
                </HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="orange"
                  onClick={() => setShowFeedbackContent(!showFeedbackContent)}
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  {showFeedbackContent ? 'Hide' : 'Show'}
                </Button>
              </HStack>
            </CardHeader>

            {showFeedbackContent && (
              <CardBody pt={0} pb={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
                <VStack spacing={4} align="stretch">
                  {!feedbackSaved ? (
                    <>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                        Share your thoughts about this exercise. Your feedback helps us create better exercises for you in the future.
                      </Text>
                      <Textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="How was this exercise? What worked well? What could be improved?"
                        size={{ base: 'sm', md: 'md' }}
                        rows={4}
                        bg="orange.50"
                        borderColor="orange.200"
                        _hover={{ borderColor: 'orange.300' }}
                        _focus={{ borderColor: 'orange.400', boxShadow: '0 0 0 1px var(--chakra-colors-orange-400)' }}
                      />
                      <Button
                        size="sm"
                        colorScheme="orange"
                        onClick={handleSaveFeedback}
                        isLoading={isSavingFeedback}
                        loadingText="Saving..."
                        alignSelf="flex-start"
                      >
                        Save Feedback
                      </Button>
                    </>
                  ) : (
                    <>
                      <HStack spacing={2} align="center">
                        <Icon as={CheckIcon} color="green.500" boxSize={4} />
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color="green.600" fontWeight="600">
                          Feedback saved! Thank you for helping us improve.
                        </Text>
                      </HStack>
                      <Box
                        bg="green.50"
                        p={{ base: 3, md: 4 }}
                        borderRadius="lg"
                        borderLeft="3px"
                        borderColor="green.400"
                      >
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.700" fontWeight="500" mb={2}>
                          Your feedback:
                        </Text>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" whiteSpace="pre-wrap">
                          {savedFeedbackText}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => {
                          setFeedbackSaved(false);
                          setFeedback(savedFeedbackText);
                        }}
                        alignSelf="flex-start"
                      >
                        Edit Feedback
                      </Button>
                    </>
                  )}
                </VStack>
              </CardBody>
            )}
          </Card>
        )}

        {/* Action Buttons */}
        <Card bg="white" shadow="lg">
          <CardBody p={{ base: 4, md: 6 }}>
            <VStack spacing={{ base: 3, md: 4 }}>
              {!practiceCompleted && !isCompleted ? (
                <VStack spacing={{ base: 3, md: 4 }} w="full">
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    colorScheme="green"
                    leftIcon={<CheckIcon />}
                    onClick={handleComplete}
                    w="full"
                    isLoading={isCompletingPractice}
                    loadingText="Completing..."
                    isDisabled={isCompletingPractice}
                  >
                    Mark as Complete
                  </Button>

                  {!navigationState.isCheckingNavigation && (
                    <HStack spacing={{ base: 2, md: 4 }} w="full">
                      {navigationState.hasPrevious && (
                        <Button
                          leftIcon={<ArrowLeftIcon />}
                          variant="outline"
                          colorScheme="brand"
                          onClick={() => handleNavigate('previous')}
                          isLoading={navigationState.isNavigating}
                          loadingText="..."
                          isDisabled={navigationState.isNavigating}
                          flex={1}
                          size={{ base: 'sm', md: 'md' }}
                        >
                          Previous
                        </Button>
                      )}

                      {navigationState.hasNext && (
                        <Button
                          rightIcon={<ArrowRightIcon />}
                          variant="outline"
                          colorScheme="brand"
                          onClick={() => handleNavigate('next')}
                          isLoading={navigationState.isNavigating}
                          loadingText="..."
                          isDisabled={navigationState.isNavigating}
                          flex={1}
                          size={{ base: 'sm', md: 'md' }}
                        >
                          Next
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              ) : (
                <VStack spacing={{ base: 3, md: 4 }} w="full">
                  <HStack spacing={2} w="full" justify="center" py={{ base: 2, md: 4 }}>
                    <Icon as={CheckIcon} color="green.500" boxSize={{ base: 5, md: 6 }} />
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="600" color="green.600">
                      Practice Completed!
                    </Text>
                  </HStack>

                  {!navigationState.isCheckingNavigation && (
                    <HStack spacing={{ base: 2, md: 4 }} w="full">
                      {navigationState.hasPrevious && (
                        <Button
                          leftIcon={<ArrowLeftIcon />}
                          variant="solid"
                          colorScheme="brand"
                          onClick={() => handleNavigate('previous')}
                          isLoading={navigationState.isNavigating}
                          loadingText="..."
                          isDisabled={navigationState.isNavigating}
                          flex={1}
                          size={{ base: 'sm', md: 'md' }}
                        >
                          Previous
                        </Button>
                      )}

                      {navigationState.hasNext && (
                        <Button
                          rightIcon={<ArrowRightIcon />}
                          variant="solid"
                          colorScheme="brand"
                          onClick={() => handleNavigate('next')}
                          isLoading={navigationState.isNavigating}
                          loadingText="..."
                          isDisabled={navigationState.isNavigating}
                          flex={1}
                          size={{ base: 'sm', md: 'md' }}
                        >
                          Next
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              )}

              <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" textAlign="center" px={2}>
                Take your time. Growth happens through consistent practice.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Reflection Modal */}
      <Modal isOpen={showReflection} onClose={() => setShowReflection(false)} size={{ base: 'full', md: 'xl' }} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }} my={{ base: 0, md: 16 }} borderRadius={{ base: 0, md: 'lg' }}>
          <ModalCloseButton color="white" />
          <Box bg="green.500" py={{ base: 4, md: 6 }}>
            <VStack spacing={2}>
              <Icon as={CheckIcon} color="white" boxSize={{ base: 6, md: 8 }} />
              <Heading size={{ base: 'md', md: 'lg' }} color="white" textAlign="center">
                Practice Complete!
              </Heading>
              <Text color="whiteAlpha.900" textAlign="center" fontSize={{ base: 'xs', md: 'sm' }}>
                Take a moment to reflect on your experience
              </Text>
            </VStack>
          </Box>

          <ModalBody p={{ base: 4, md: 6 }}>
            <VStack spacing={{ base: 4, md: 6 }} align="stretch">
              <Box>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600" mb={{ base: 2, md: 3 }} fontWeight="600">
                  How did this practice feel for you? (Optional)
                </Text>
                <Textarea
                  placeholder="Share any insights, feelings, or observations..."
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  rows={4}
                  resize="vertical"
                  fontSize={{ base: 'sm', md: 'md' }}
                />
              </Box>

              <VStack spacing={3} w="full">
                <Button
                  colorScheme="green"
                  onClick={() => {
                    setShowReflection(false);
                    toast({
                      title: "Reflection Saved",
                      description: "Thank you for taking time to reflect.",
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  }}
                  w="full"
                  size={{ base: 'md', md: 'lg' }}
                >
                  Save Reflection
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowReflection(false)}
                  w="full"
                  size={{ base: 'sm', md: 'md' }}
                >
                  Skip for Now
                </Button>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  )
}

// ModernSidebar component moved to SharedComponents.js

// UserMenu component moved to SharedComponents.js

export default PracticeView